import { useQuery } from '@apollo/client';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { Button, Field, Persona, ProgressBar } from '@fluentui/react-components';
import { mergeStyles } from '@fluentui/react';
import { EditRegular, InfoRegular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  bodyContentContainer,
  iconButtonStyles,
} from '../../../app/common/styles/CommonStyleObjects';
import momentUtc from '../../../utils/DateFormatter';
import { GET_COMPANY_CREATION_PROGRESS } from '../../../utils/statApi/CompanyCreationApi';
import StatDateFormats from '../../../utils/types/DateFormats';
import CanAccess from '../../common/canAccess/CanAccess';
import Card from '../../common/card/Card';
import { ICompanyCreationParams } from '../../common/ParamTypes';
import SpanWithLabel from '../../common/SpanWithLabel';
import StackColumn from '../../common/StackColumn';
import { IServiceContact } from '../../companies/serviceContacts/interfaces';
import ServiceContactList from '../../companies/serviceContacts/ServiceContactList';
import ActivateCompanyReviewPanel from '../ActivateCompanyReviewPanel';
import CompanyCodePanel from '../CompanyCodePanel';
import CompleteSubledgerReviewPanel from '../CompleteSubledgerReviewPanel';
import NewCompanyPanel from '../NewCompanyPanel';
import SubmitToSapReviewPanel from '../SubmitToSapReviewPanel';
import {
  CompanyCreationStatus,
  FinancialType,
  ICompanyCreationRequestDetails,
  OverviewForm,
} from '../types';
import SapProgressPanel from './SapProgressPanel';
import ShadowCompanyCodePanel from './ShadowCompanyCodePanel';
import StatProgressPanel from './StatProgressPanel';
import SubledgerProgressPanel from './SubledgerProgressPanel';

interface ICompanyInformationPageState {
  isEditPanelOpen: boolean;
  currentForm?: OverviewForm;
}

interface ICompanyRequestDetailPageProps {
  loading: boolean;
  companyRequestInitialInfo: ICompanyCreationRequestDetails;
}

const OverviewPage = ({
  companyRequestInitialInfo,
  loading,
}: ICompanyRequestDetailPageProps): JSX.Element => {
  const [panelState, setPanelState] = useState<ICompanyInformationPageState>({
    isEditPanelOpen: false,
    currentForm: null,
  });

  const { id } = useParams<ICompanyCreationParams>();
  const companyCreationId = parseInt(id, 10);
  const isRequestReadOnly =
    companyRequestInitialInfo?.statusId === CompanyCreationStatus.Canceled ||
    companyRequestInitialInfo?.statusId === CompanyCreationStatus.Complete;

  const isNonTransactional =
    companyRequestInitialInfo?.financialType?.id === FinancialType.NonTransactional;

  const isShadowCompanyRequest =
    companyRequestInitialInfo?.financialType?.id === FinancialType.ShadowCompany;

  const { data: progressData, loading: progressLoading } = useQuery(GET_COMPANY_CREATION_PROGRESS, {
    variables: {
      companyCreationId,
    },
  });

  const closePanel = (): void => {
    setPanelState({ isEditPanelOpen: false, currentForm: null });
  };

  const davaDealLead: IServiceContact[] = companyRequestInitialInfo?.davaDealLead?.displayName
    ? [
        {
          ...companyRequestInitialInfo?.davaDealLead,
          id: companyRequestInitialInfo?.davaDealLead.id.toString(),
          name: companyRequestInitialInfo?.davaDealLead.displayName,
          text: companyRequestInitialInfo?.davaDealLead.displayName,
          graphId: companyRequestInitialInfo?.davaDealLead.graphGUID.toString(),
          email: '',
          contactType: 'Dava Deal Lead',
        },
      ]
    : [];

  const requestor: IServiceContact[] = [
    {
      ...companyRequestInitialInfo?.requestor,
      id: companyRequestInitialInfo?.requestor.id.toString(),
      name: companyRequestInitialInfo?.requestor.displayName,
      text: companyRequestInitialInfo?.requestor.displayName,
      graphId: companyRequestInitialInfo?.requestor.graphId.toString(),
      email: '',
      contactType: 'Requestor',
    },
  ];

  const companyCodeRequestor = {
    id: progressData?.companyCreationProgress?.lastCompanyCodeRequest?.sender.id.toString(),
    name: progressData?.companyCreationProgress?.lastCompanyCodeRequest?.sender.displayName,
    graphId: progressData?.companyCreationProgress?.lastCompanyCodeRequest?.sender.toString(),
  };

  const buttonStyle = {
    root: { marginRight: 0, minWidth: '200px' },
    rootHovered: { border: 'none' },
  };

  const progressBarClassName = mergeStyles({ maxWidth: '50%' });

  const renderActionButton = (label: string, formType: OverviewForm): JSX.Element => {
    return (
      <>
        {!isRequestReadOnly && !isShadowCompanyRequest && (
          <CanAccess requestedAction="CompanyCreation:Edit">
            <Button
              appearance="subtle"
              title={`Edit ${label}`}
              aria-label={label}
              icon={<EditRegular color={iconButtonStyles.color} />}
              onClick={(): void => {
                setPanelState({
                  isEditPanelOpen: true,
                  currentForm: formType,
                });
              }}
            />
          </CanAccess>
        )}
      </>
    );
  };

  const renderEditCompanyCodeActionButton = (requestedAction: string): JSX.Element => {
    return (
      <>
        {!isRequestReadOnly && (
          <CanAccess requestedAction={requestedAction}>
            <Button
              appearance="subtle"
              title="Edit Company Code"
              aria-label="Company Code"
              icon={<EditRegular color={iconButtonStyles.color} />}
              onClick={(): void => {
                setPanelState({
                  isEditPanelOpen: true,
                  currentForm: OverviewForm.CompanyCode,
                });
              }}
            />
          </CanAccess>
        )}
      </>
    );
  };

  const getSAPSetupLabel = (): JSX.Element => {
    return (
      <>
        <span>SAP Configuration Requirements Progress</span>
        <Button
          appearance="subtle"
          id="sapProgressButton"
          aria-describedby="tooltip"
          aria-label="SAP required fields"
          icon={<InfoRegular color={iconButtonStyles.color} />}
          onClick={() => {
            setPanelState({
              isEditPanelOpen: true,
              currentForm: OverviewForm.SubmitSAPProgress,
            });
          }}
        />
      </>
    );
  };

  const getSubledgerSetupLabel = (): JSX.Element => {
    return (
      <>
        <span>Subledger Configuration Requirements Progress</span>
        <Button
          appearance="subtle"
          id="subledgerProgressButton"
          aria-describedby="tooltip"
          aria-label="Subledger required fields"
          icon={<InfoRegular color={iconButtonStyles.color} />}
          onClick={() => {
            setPanelState({
              isEditPanelOpen: true,
              currentForm: OverviewForm.SubmitSubledgerProgress,
            });
          }}
        />
      </>
    );
  };

  const getStatSetupLabel = (): JSX.Element => {
    return (
      <>
        <span>Stat Tool Setup Progress</span>
        <Button
          appearance="subtle"
          id="setupProgressButton"
          aria-describedby="tooltip"
          aria-label="Stat Progress required fields"
          icon={<InfoRegular color={iconButtonStyles.color} />}
          onClick={() => {
            setPanelState({
              isEditPanelOpen: true,
              currentForm: OverviewForm.SubmitStatProgress,
            });
          }}
        />
      </>
    );
  };

  const renderCompanyCodeEditButton = (): JSX.Element => {
    // Adding new company code
    if (
      !companyRequestInitialInfo?.companyCode &&
      companyRequestInitialInfo?.statusId === CompanyCreationStatus.CompanyCodeRequested
    ) {
      return renderEditCompanyCodeActionButton('CompanyCreation:Edit');
    }
    // Updating existing company code
    if (
      companyRequestInitialInfo?.companyCode &&
      companyRequestInitialInfo?.statusId !== CompanyCreationStatus.Canceled &&
      companyRequestInitialInfo?.statusId !== CompanyCreationStatus.Complete
    ) {
      return renderEditCompanyCodeActionButton('CompanyCreation:EditCompanyCode');
    }
    return <></>;
  };

  const renderShadowCompanyCodeActionButton = (): JSX.Element => {
    return (
      <>
        {!isRequestReadOnly &&
          (isShadowCompanyRequest ||
            progressData?.companyCreationProgress?.lastCompanyCodeRequest?.sentTime) && (
            <CanAccess
              requestedAction={
                isShadowCompanyRequest
                  ? 'CompanyCreation:CompleteShadowCompany'
                  : 'CompanyCreation:Edit'
              }
            >
              <Button
                appearance="subtle"
                title="Edit Shadow Company Code"
                aria-label="Shadow Company Code"
                icon={<EditRegular color={iconButtonStyles.color} />}
                onClick={(): void => {
                  setPanelState({
                    isEditPanelOpen: true,
                    currentForm: OverviewForm.ShadowCompanyCode,
                  });
                }}
              />
            </CanAccess>
          )}
      </>
    );
  };

  return (
    <div className={`${bodyContentContainer} lightBackground`}>
      <Stack horizontal wrap horizontalAlign="space-between" tokens={{ childrenGap: 20 }}>
        {!isShadowCompanyRequest && (
          <StackColumn>
            <Card
              title="Company Code"
              headingLevel={2}
              isLoading={loading}
              actionButton={renderCompanyCodeEditButton()}
            >
              <Stack horizontal wrap horizontalAlign="space-between">
                <StackColumn>
                  <SpanWithLabel
                    labelText="Company Code"
                    value={companyRequestInitialInfo?.companyCode}
                  />
                </StackColumn>
              </Stack>
            </Card>
          </StackColumn>
        )}
        {!isNonTransactional && (
          <StackColumn>
            <Card
              title="Shadow Company Code"
              headingLevel={2}
              isLoading={loading}
              actionButton={renderShadowCompanyCodeActionButton()}
            >
              <StackColumn>
                <SpanWithLabel
                  labelText="Shadow Company Code"
                  value={companyRequestInitialInfo?.shadowCompanyCode}
                />
              </StackColumn>
            </Card>
          </StackColumn>
        )}
      </Stack>
      <br />
      <Card
        title="Initial Company Creation Info"
        headingLevel={2}
        isLoading={loading}
        actionButton={renderActionButton('Initial Company Creation Info', OverviewForm.InitialInfo)}
      >
        <Stack horizontal wrap horizontalAlign="space-between">
          <StackColumn>
            {!isShadowCompanyRequest && (
              <>
                <SpanWithLabel
                  labelText="Confidential"
                  value={companyRequestInitialInfo?.isConfidential}
                />
                <SpanWithLabel
                  labelText="Acquisition Name"
                  value={companyRequestInitialInfo?.acquisition.text}
                />
                <SpanWithLabel
                  labelText="Acquisition Date"
                  value={momentUtc(
                    companyRequestInitialInfo?.dateOfAcquisition,
                    StatDateFormats.DayBreakout,
                    true,
                  )}
                />
              </>
            )}
            <SpanWithLabel
              labelText="Financial Type"
              value={companyRequestInitialInfo?.financialType.fieldName}
            />
            <SpanWithLabel
              labelText="Entity Type"
              value={companyRequestInitialInfo?.entityType.fieldName}
            />
            <SpanWithLabel
              labelText="Business Nature of Entity"
              value={companyRequestInitialInfo?.businessNatureOfEntity}
            />
          </StackColumn>
          <StackColumn>
            <SpanWithLabel
              labelText="Status"
              value={companyRequestInitialInfo?.status.replace(/([A-Z])/g, ' $1').trim()}
            />
            <ServiceContactList labelText="Requestor" contacts={requestor} />
            {!isShadowCompanyRequest && (
              <ServiceContactList labelText="DAVA Deal Lead" contacts={davaDealLead} />
            )}
          </StackColumn>
        </Stack>
      </Card>
      <br />
      {!isShadowCompanyRequest && (
        <Card title="Company Creation Progress" headingLevel={2} isLoading={progressLoading}>
          <Stack wrap horizontalAlign="space-between" tokens={{ childrenGap: 20 }}>
            <StackItem className={progressBarClassName}>
              <Field
                label="Overall Progress"
                hint={`${progressData?.companyCreationProgress?.overallPercentComplete}% Complete`}
              >
                <ProgressBar
                  value={progressData?.companyCreationProgress?.overallPercentComplete / 100 || 0}
                />
              </Field>
            </StackItem>
            {!isNonTransactional && (
              <>
                <Stack
                  horizontal
                  horizontalAlign="start"
                  verticalAlign="center"
                  tokens={{ childrenGap: 30 }}
                  wrap
                >
                  <StackItem grow className={progressBarClassName}>
                    <Field
                      label={getSAPSetupLabel()}
                      hint={`${progressData?.companyCreationProgress?.sapPercentComplete}% Complete`}
                    >
                      <ProgressBar
                        value={progressData?.companyCreationProgress?.sapPercentComplete / 100 || 0}
                      />
                    </Field>
                  </StackItem>
                  <CanAccess requestedAction="CompanyCreation:RequestCompanyCode">
                    <StackItem>
                      <Button
                        appearance="primary"
                        aria-label="Request Company Code"
                        style={buttonStyle.root}
                        onClick={(): void => {
                          setPanelState({
                            isEditPanelOpen: true,
                            currentForm: OverviewForm.SubmitSAP,
                          });
                        }}
                        disabled={
                          companyRequestInitialInfo?.companyCode > 0 ||
                          progressData?.companyCreationProgress?.sapPercentComplete !== 100 ||
                          isRequestReadOnly
                        }
                      >
                        Request Company Code
                      </Button>
                    </StackItem>
                  </CanAccess>
                  <StackItem>
                    <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                      <SpanWithLabel
                        labelText="Company Code Last Requested"
                        value={momentUtc(
                          progressData?.companyCreationProgress?.lastCompanyCodeRequest?.sentTime ||
                            '',
                          StatDateFormats.DayBreakout,
                          true,
                        )}
                      />
                      {progressData?.companyCreationProgress?.lastCompanyCodeRequest?.sentTime && (
                        <>
                          <span>By</span>
                          <Persona
                            key={companyCodeRequestor.id}
                            name={companyCodeRequestor.name}
                            avatar={{ size: 40, color: 'colorful' }}
                            size="medium"
                            primaryText={{ style: { paddingTop: '10px' } }}
                          />
                        </>
                      )}
                    </Stack>
                  </StackItem>
                </Stack>
                <Stack
                  horizontal
                  horizontalAlign="start"
                  verticalAlign="center"
                  tokens={{ childrenGap: 30 }}
                  wrap
                >
                  <StackItem grow className={progressBarClassName}>
                    <Field
                      label={getSubledgerSetupLabel()}
                      hint={`${progressData?.companyCreationProgress?.subledgerPercentComplete}% Complete`}
                    >
                      <ProgressBar
                        value={
                          progressData?.companyCreationProgress?.subledgerPercentComplete / 100 || 0
                        }
                      />
                    </Field>
                  </StackItem>
                  <CanAccess requestedAction="CompanyCreation:CompleteSubledger">
                    <StackItem>
                      <Button
                        appearance="primary"
                        aria-label="Complete Subledger Info"
                        style={buttonStyle.root}
                        onClick={(): void => {
                          setPanelState({
                            isEditPanelOpen: true,
                            currentForm: OverviewForm.CompleteSubledger,
                          });
                        }}
                        disabled={
                          companyRequestInitialInfo?.statusId !==
                            CompanyCreationStatus.PendingSubledgerInfo ||
                          progressData?.companyCreationProgress?.subledgerPercentComplete !== 100 ||
                          isRequestReadOnly
                        }
                      >
                        Complete Subledger Info
                      </Button>
                    </StackItem>
                  </CanAccess>
                  <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                    <SpanWithLabel
                      labelText="System Setup Sent"
                      value={momentUtc(
                        progressData?.companyCreationProgress?.lastSystemSetup?.sentTime || '',
                        StatDateFormats.DayBreakout,
                        true,
                      )}
                    />
                    {progressData?.companyCreationProgress?.lastSystemSetup?.sentTime && (
                      <>
                        <span>By</span>
                        <Persona
                          key={progressData?.companyCreationProgress?.lastSystemSetup?.sender?.id}
                          name={
                            progressData?.companyCreationProgress?.lastSystemSetup?.sender
                              ?.displayName
                          }
                          avatar={{ size: 40, color: 'colorful' }}
                          size="medium"
                        />
                      </>
                    )}
                  </Stack>
                </Stack>
              </>
            )}
            <Stack
              horizontal
              horizontalAlign="start"
              verticalAlign="center"
              tokens={{ childrenGap: 30 }}
              wrap
            >
              <StackItem grow className={progressBarClassName}>
                <Field
                  label={getStatSetupLabel()}
                  hint={`${progressData?.companyCreationProgress?.statPercentComplete}% Complete`}
                >
                  <ProgressBar
                    value={progressData?.companyCreationProgress?.statPercentComplete / 100 || 0}
                  />
                </Field>
              </StackItem>
              <CanAccess requestedAction="CompanyCreation:Activate">
                <StackItem>
                  <Button
                    appearance="primary"
                    aria-label="Activate in Stat"
                    style={buttonStyle.root}
                    onClick={(): void => {
                      setPanelState({
                        isEditPanelOpen: true,
                        currentForm: OverviewForm.ActivateCompany,
                      });
                    }}
                    disabled={
                      companyRequestInitialInfo?.statusId !==
                        CompanyCreationStatus.PendingStatToolSetup ||
                      progressData?.companyCreationProgress?.statPercentComplete !== 100 ||
                      isRequestReadOnly
                    }
                  >
                    Activate in Stat
                  </Button>
                </StackItem>
              </CanAccess>
              <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                <SpanWithLabel
                  labelText="Date Activated in STAT"
                  value={momentUtc(
                    progressData?.companyCreationProgress?.dateActivated?.sentTime || '',
                    StatDateFormats.DayBreakout,
                    true,
                  )}
                />
                {progressData?.companyCreationProgress?.dateActivated?.sentTime && (
                  <>
                    <span>By</span>
                    <Persona
                      key={progressData?.companyCreationProgress?.dateActivated?.sender?.id}
                      name={
                        progressData?.companyCreationProgress?.dateActivated?.sender?.displayName
                      }
                      avatar={{ size: 40, color: 'colorful' }}
                      size="medium"
                    />
                  </>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Card>
      )}
      {isShadowCompanyRequest && (
        <Card title="Shadow Company Info" headingLevel={2} isLoading={loading}>
          <Stack horizontal wrap horizontalAlign="space-between">
            <StackColumn>
              <SpanWithLabel
                labelText="Parent Company Code"
                value={companyRequestInitialInfo?.parentCompanyCode}
              />
            </StackColumn>
            <StackColumn>
              <SpanWithLabel
                labelText="Shadow Company Name"
                value={companyRequestInitialInfo?.companyName}
              />
            </StackColumn>
          </Stack>
        </Card>
      )}
      {panelState.isEditPanelOpen && panelState.currentForm === OverviewForm.InitialInfo && (
        <NewCompanyPanel
          closePanel={closePanel}
          companyCreationRequest={companyRequestInitialInfo}
        />
      )}
      {panelState.isEditPanelOpen && panelState.currentForm === OverviewForm.SubmitSAP && (
        <SubmitToSapReviewPanel closePanel={closePanel} />
      )}
      {panelState.isEditPanelOpen && panelState.currentForm === OverviewForm.CompleteSubledger && (
        <CompleteSubledgerReviewPanel closePanel={closePanel} />
      )}
      {panelState.isEditPanelOpen && panelState.currentForm === OverviewForm.ActivateCompany && (
        <ActivateCompanyReviewPanel closePanel={closePanel} />
      )}
      {panelState.isEditPanelOpen && panelState.currentForm === OverviewForm.SubmitSAPProgress && (
        <SapProgressPanel closePanel={closePanel} />
      )}
      {panelState.isEditPanelOpen && panelState.currentForm === OverviewForm.CompanyCode && (
        <CompanyCodePanel
          closePanel={closePanel}
          companyCreationRequestData={companyRequestInitialInfo}
        />
      )}
      {panelState.isEditPanelOpen && panelState.currentForm === OverviewForm.SubmitStatProgress && (
        <StatProgressPanel
          closePanel={closePanel}
          companyCode={companyRequestInitialInfo?.companyCode}
          shadowCompanyCode={companyRequestInitialInfo?.shadowCompanyCode}
          isNonTransactional={isNonTransactional}
        />
      )}
      {panelState.isEditPanelOpen &&
        panelState.currentForm === OverviewForm.SubmitSubledgerProgress && (
          <SubledgerProgressPanel
            closePanel={closePanel}
            companyCode={companyRequestInitialInfo?.companyCode}
          />
        )}
      {panelState.isEditPanelOpen && panelState.currentForm === OverviewForm.ShadowCompanyCode && (
        <ShadowCompanyCodePanel
          closePanel={closePanel}
          companyCreationRequestData={companyRequestInitialInfo}
        />
      )}
    </div>
  );
};

export default OverviewPage;
